import { toast } from 'sonner';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { useTheme } from '@mui/material/styles';
import {
  Box,
  Stack,
  Button,
  Dialog,
  Avatar,
  Typography,
  IconButton,
  DialogContent,
} from '@mui/material';

import { fDateTime } from 'src/utils/format-time';

import { varAlpha } from 'src/theme/styles';
import { useNotificationHubContext } from 'src/contexts/notification-context';
import { useNotificationOperations } from 'src/api/services/system_definition/systemService';

import { Iconify } from 'src/components/iconify';

import { getIconConfig } from './icon-config';
import { renderMessageWithLinks } from './render-message-with-links';

// ----------------------------------------------------------------------

const SESSION_KEY = 'login_notification_modal_shown';

export function LoginNotificationModal() {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const { notifications, markAsRead } = useNotificationHubContext();
  const { readNotification } = useNotificationOperations();

  const latestUnread = useMemo(() => {
    if (!notifications || notifications.length === 0) return null;

    const unreadList = notifications.filter((n) => !n.isRead);
    if (unreadList.length === 0) return null;

    return unreadList.reduce((latest, current) => {
      const latestDate = new Date(latest.createdAt);
      const currentDate = new Date(current.createdAt);
      return currentDate > latestDate ? current : latest;
    }, unreadList[0]);
  }, [notifications]);

  useEffect(() => {
    if (!latestUnread) return;
    if (sessionStorage.getItem(SESSION_KEY)) return;

    setOpen(true);
    sessionStorage.setItem(SESSION_KEY, 'true');
  }, [latestUnread]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleMarkAsRead = useCallback(async () => {
    if (!latestUnread) return;

    setLoading(true);
    try {
      await readNotification(latestUnread.id);
      markAsRead(latestUnread.id);
      toast.success('Notification marked as read');
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      toast.error('Failed to mark notification');
    } finally {
      setLoading(false);
      setOpen(false);
    }
  }, [latestUnread, readNotification, markAsRead]);

  const handleLinkClick = useCallback(() => {
    if (!latestUnread || latestUnread.isRead) {
      setOpen(false);
      return;
    }
    readNotification(latestUnread.id).catch((error) =>
      console.error('Failed to mark notification as read:', error)
    );
    markAsRead(latestUnread.id);
    setOpen(false);
  }, [latestUnread, readNotification, markAsRead]);

  if (!latestUnread) return null;

  const config = getIconConfig(latestUnread.type);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          px: 4,
          py: 5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: `linear-gradient(135deg, ${theme.vars.palette[latestUnread.type?.toLowerCase() === 'error' ? 'error' : latestUnread.type?.toLowerCase() === 'warning' ? 'warning' : latestUnread.type?.toLowerCase() === 'success' ? 'success' : 'info'].main} 0%, ${theme.vars.palette[latestUnread.type?.toLowerCase() === 'error' ? 'error' : latestUnread.type?.toLowerCase() === 'warning' ? 'warning' : latestUnread.type?.toLowerCase() === 'success' ? 'success' : 'info'].dark} 100%)`,
          color: '#fff',
        }}
      >
        <Stack direction="row" spacing={2.5} alignItems="center">
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: varAlpha('255 255 255', 0.16),
              color: '#fff',
            }}
          >
            <Iconify icon={config.icon} width={32} />
          </Avatar>

          <Box>
            <Typography variant="overline" sx={{ opacity: 0.72, letterSpacing: 1.2 }}>
              New Notification
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.25 }}>
              {latestUnread.title}
            </Typography>
          </Box>
        </Stack>

        <IconButton onClick={handleClose} sx={{ color: '#fff' }}>
          <Iconify icon="mingcute:close-line" width={22} />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 4, py: 4 }}>
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
            <Iconify icon="solar:calendar-bold" width={18} />
            <Typography variant="caption">{fDateTime(latestUnread.createdAt)}</Typography>
          </Stack>

          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: 'background.neutral',
              maxHeight: 350,
              overflow: 'auto',
            }}
          >
            <Typography
              variant="body1"
              sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, wordBreak: 'break-word' }}
            >
              {renderMessageWithLinks(latestUnread.message, { onLinkClick: handleLinkClick })}
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 1 }}>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              onClick={handleClose}
              startIcon={<Iconify icon="mingcute:close-line" />}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleMarkAsRead}
              loading={loading}
              startIcon={<Iconify icon="solar:check-read-bold" />}
            >
              Mark As Read
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}